import { HID_WALLET_PASS_STATUS } from './constants';

export type WalletCredential = {
    passId?: string;
    walletRequestId?: string;
    credentialContainerId?: string;
    hidCredentialId?: string;
    cardNumber?: string;
    platform?: string;
    deviceType?: string;
    status?: string;
    suspendSource?: string;
    suspendReason?: string;
    lastEventType?: string;
    inceptionSyncStatus?: string;
    createdAt?: string;
    updatedAt?: string;
};

export const getWalletCredentialKey = (credential: WalletCredential, index = 0) =>
    credential.passId ||
    credential.walletRequestId ||
    credential.hidCredentialId ||
    credential.cardNumber ||
    `wallet-credential-${index}`;

const TERMINAL_WALLET_STATUSES = [
    HID_WALLET_PASS_STATUS.REVOKED,
    HID_WALLET_PASS_STATUS.REMOVED,
    HID_WALLET_PASS_STATUS.FAILED,
    'EXPIRED',
];

const isTerminalWalletStatus = (status?: string) =>
    TERMINAL_WALLET_STATUSES.includes(`${status || ''}`.toUpperCase());

export const getWalletPlatformLabel = (platform?: string, deviceType?: string, status?: string) => {
    const normalizedPlatform = `${platform || ''}`.toUpperCase();
    const normalizedDeviceType = `${deviceType || ''}`.toUpperCase();
    const unresolvedDeviceLabel = isTerminalWalletStatus(status) ? 'device unknown' : 'device pending';
    if (normalizedPlatform === 'APPLE') {
        if (normalizedDeviceType === 'PHONE') return 'iPhone';
        if (normalizedDeviceType === 'WATCH') return 'Apple Watch';
        return `Apple Wallet — ${unresolvedDeviceLabel}`;
    }
    if (normalizedPlatform === 'GOOGLE') {
        if (normalizedDeviceType === 'WATCH') return 'Google Wallet Watch';
        if (normalizedDeviceType === 'PHONE') return 'Google Wallet';
        return `Google Wallet — ${unresolvedDeviceLabel}`;
    }

    const platformLabel = platform || 'Wallet';
    if (normalizedDeviceType === 'WATCH') return `${platformLabel} Watch`;
    if (normalizedDeviceType === 'PHONE') return `${platformLabel} Phone`;
    return `${platformLabel} — ${unresolvedDeviceLabel}`;
};

export const getWalletStatusLabel = (status?: string) => {
    switch (`${status || ''}`.toUpperCase()) {
        case HID_WALLET_PASS_STATUS.ACTIVE:
            return 'Active';
        case HID_WALLET_PASS_STATUS.SUSPENDING:
            return 'Suspending';
        case HID_WALLET_PASS_STATUS.SUSPENDED:
            return 'Suspended';
        case HID_WALLET_PASS_STATUS.RESUMING:
            return 'Resuming';
        case HID_WALLET_PASS_STATUS.REVOKING:
            return 'Revoking';
        case HID_WALLET_PASS_STATUS.REVOKED:
            return 'Revoked';
        case HID_WALLET_PASS_STATUS.PROVISIONING:
            return 'Provisioning';
        case HID_WALLET_PASS_STATUS.ISSUANCE_CREATED:
            return 'Issuance created';
        case HID_WALLET_PASS_STATUS.FAILED:
            return 'Failed';
        case HID_WALLET_PASS_STATUS.REMOVED:
            return 'Removed';
        default:
            return status || 'Status pending';
    }
};

export const getWalletSuspendSourceLabel = (credential: WalletCredential) => {
    const source = `${credential.suspendSource || ''}`.toUpperCase();
    const eventType = `${credential.lastEventType || credential.suspendReason || ''}`.toUpperCase();

    if (source === 'ADMIN_WEBAPP') return 'Admin';
    if (source === 'USER_WALLET' || eventType.includes('USER') || eventType.includes('LOST')) return 'Wallet/iCloud';
    if (source === 'HID_CALLBACK') return 'HID';
    return undefined;
};

export const getWalletStatusText = (credential: WalletCredential) => {
    const statusLabel = getWalletStatusLabel(credential.status);
    const normalizedStatus = `${credential.status || ''}`.toUpperCase();
    const sourceLabel = [HID_WALLET_PASS_STATUS.SUSPENDING, HID_WALLET_PASS_STATUS.SUSPENDED].includes(normalizedStatus)
        ? getWalletSuspendSourceLabel(credential)
        : undefined;
    return [statusLabel, sourceLabel].filter(Boolean).join(' — ');
};

export const getWalletStatusColor = (status?: string) => {
    switch (`${status || ''}`.toUpperCase()) {
        case HID_WALLET_PASS_STATUS.ACTIVE:
            return '#389e0d';
        case HID_WALLET_PASS_STATUS.SUSPENDED:
        case HID_WALLET_PASS_STATUS.SUSPENDING:
            return '#d48806';
        case HID_WALLET_PASS_STATUS.RESUMING:
            return '#1677ff';
        case HID_WALLET_PASS_STATUS.REVOKING:
        case HID_WALLET_PASS_STATUS.REVOKED:
        case HID_WALLET_PASS_STATUS.FAILED:
            return '#cf1322';
        default:
            return '#8c8c8c';
    }
};

export const getWalletCredentialDescription = (credential: WalletCredential) => {
    const card = credential.cardNumber ? `card ${credential.cardNumber}` : undefined;
    return [getWalletPlatformLabel(credential.platform, credential.deviceType, credential.status), card].filter(Boolean).join(' — ');
};

export const canSuspendWalletCredential = (credential: WalletCredential) =>
    !!credential.passId && `${credential.status || ''}`.toUpperCase() === HID_WALLET_PASS_STATUS.ACTIVE;

export const canResumeWalletCredential = (credential: WalletCredential) =>
    !!credential.passId && `${credential.status || ''}`.toUpperCase() === HID_WALLET_PASS_STATUS.SUSPENDED;

export const canRevokeWalletCredential = (credential: WalletCredential) =>
    !!credential.passId && [
        HID_WALLET_PASS_STATUS.ISSUANCE_CREATED,
        HID_WALLET_PASS_STATUS.PROVISIONING,
        HID_WALLET_PASS_STATUS.ACTIVE,
        HID_WALLET_PASS_STATUS.SUSPENDED,
    ].includes(`${credential.status || ''}`.toUpperCase());

export const isTerminalWalletCredential = (credential: WalletCredential) =>
    isTerminalWalletStatus(credential.status);
